import React, { useEffect, useState, useRef } from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Button, ActionButton, SmallInfoCard } from '@bloomays-lib/ui.shared';
import { FormCRA } from '../molecules/FormCRA';
import { convertActivityToEachDaysArray, splitEmbedSignature, sanitizeFileName } from '@bloomays-lib/utils.shared';
import CRASignature, { BaseSignatureData } from '../molecules/CRASignature';
import { ActivitySummary } from '../../types/components';
import { Logger } from '../../services/serviceLogger';
import { LogoBloomays } from '@bloomays-lib/ui.shared';
import pdfMake from 'pdfmake/build/pdfmake';
import html2canvas from 'html2canvas';
import { vfs } from '@bloomays-lib/ui.shared';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';

pdfMake.vfs = vfs;

pdfMake.fonts = {
  Poppins: {
    normal: 'Poppins-Regular.woff2',
  },
};

const logger = Logger('CRAValidation');

export interface CRAValidationProps {
  activitySummary: ActivitySummary;
  readOnly?: boolean;
  onBloomerSign?: (signatureData: BloomerSignatureData) => Promise<void>;
  onClientSign?: (signatureData: ClientSignatureData) => Promise<void>;
  onClose?: () => void;
}

export interface BloomerSignatureData {
  totalDaysWorked: number;
  signerName: string;
  signatureDate: string;
}

export interface ClientSignatureData {
  totalDaysWorked: number;
  signerName: string;
  signatureDate: string;
}

const Container = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  maxWidth: '1000px',
  margin: '0 auto',
  backgroundColor: 'white',
  minHeight: '100vh',
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  position: 'relative',
}));

const LogoSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const MissionInfoSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  boxShadow: '2px 2px 20px lightgrey',
  borderRadius: '10px',
  height: '140px',
}));

const SignatureSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

const StyledCRASignature = styled('div')(({ theme }) => ({
  boxShadow: '2px 2px 20px lightgrey',
  borderRadius: '10px',
  width: '100%',
}));

// Signature UI déléguée à CRASignature (molécule)

const CRAValidation: React.FC<CRAValidationProps> = ({
  activitySummary,
  readOnly = false,
  onBloomerSign,
  onClientSign,
  onClose,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [bloomerSignature, setBloomerSignature] = useState<BloomerSignatureData>({
    totalDaysWorked: 0,
    signerName: '',
    signatureDate: '',
  });

  const [clientSignature, setClientSignature] = useState<ClientSignatureData>({
    totalDaysWorked: 0,
    signerName: '',
    signatureDate: '',
  });

  const [isBloomerSigned, setIsBloomerSigned] = useState(false);
  const [isClientSigned, setIsClientSigned] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Calculer les données pour SummaryDaysWorked
  const totalDays = activitySummary.nbTotalDaysWorked;

  useEffect(() => {
    if ((activitySummary.signedCount || 0) >= 1) {
      setIsBloomerSigned(true);
    }

    if ((activitySummary.signedCount || 0) >= 2) {
      setIsClientSigned(true);
    }

    if (activitySummary.signingEmbedBloomerId) {
      const { signDate, signerIdentity } = splitEmbedSignature(activitySummary.signingEmbedBloomerId);
      setBloomerSignature({
        signerName: signerIdentity,
        signatureDate: signDate?.toISOString() || '',
        totalDaysWorked: totalDays,
      });
    }

    if (activitySummary.signingEmbedClientId) {
      const { signDate, signerIdentity } = splitEmbedSignature(activitySummary.signingEmbedClientId);
      setClientSignature({
        signerName: signerIdentity,
        signatureDate: signDate?.toISOString() || '',
        totalDaysWorked: totalDays,
      });
    }
  }, [activitySummary, totalDays]);

  const activityDays = convertActivityToEachDaysArray(
    activitySummary.date.toISOString(),
    activitySummary.nbDaysDetails,
  );

  const handleBloomerSign = async (data: BloomerSignatureData) => {
    if (!onBloomerSign) return;

    setLoading(true);
    try {
      await onBloomerSign({
        ...data,
        signatureDate: new Date().toISOString(),
      });
      setIsBloomerSigned(true);
    } catch (error) {
      logger.error('Erreur lors de la signature bloomer:', { activitySummary }, error as Error);
    } finally {
      setLoading(false);
    }
  };

  const handleClientSign = async (data: ClientSignatureData) => {
    if (!onClientSign) return;

    setLoading(true);
    try {
      await onClientSign(data);
      setIsClientSigned(true);
    } catch (error) {
      logger.error('Erreur lors de la signature client:', { activitySummary }, error as Error);
    } finally {
      setLoading(false);
    }
  };

  const generatePdf = async (containerElement: HTMLDivElement) => {
    const canvas = await html2canvas(containerElement, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      ignoreElements: (element) => element.classList.contains('pdf-hidden'),
    });

    const imgWidth = 400; // A4 width in mm

    const imgData = canvas.toDataURL();
    const imgObj = {
      image: imgData,
      width: imgWidth,
      style: {
        alignment: 'center',
      },
    };
    const documentDefinition = {
      content: [imgObj],
      defaultStyle: {
        font: 'Poppins',
      },
      pageSize: 'A4',
      pageOrientation: 'portrait',
      pageMargins: [0, 0, 0, 0],
    };
    const fileName = `Bloomays_CRA_${activitySummary.clientSocietyName}_${activitySummary.monthString}_${activitySummary.year}_${activitySummary.bloomerFullName}.pdf`;
    const pdfDocGenerator = pdfMake.createPdf(documentDefinition);
    pdfDocGenerator.download(sanitizeFileName(fileName));
  };

  const handleDownloadPDF = async () => {
    if (!containerRef.current) return;

    setPdfLoading(true);
    try {
      await generatePdf(containerRef.current);
    } catch (error) {
      logger.error('Erreur lors de la génération du PDF:', { activitySummary }, error as Error);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .cra-print-area, .cra-print-area * { visibility: visible !important; }
          .cra-print-area { position: static !important; left: auto; top: auto; width: 100% !important; }
          .pdf-hidden { display: none !important; }
          @page { size: A4; margin: 10mm; }
        }
      `}</style>
      <Container ref={containerRef} className="cra-print-area">
        <HeaderSection>
          <LogoSection>
            <Box>
              <LogoBloomays alt="Bloomays" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="heading3">
                COMPTE RENDU D'ACTIVITÉ - {activitySummary.monthString} {activitySummary.year}
              </Typography>
              {activitySummary.validated && (
                <Box className="pdf-hidden">
                  <ActionButton
                    onClick={handleDownloadPDF}
                    loading={pdfLoading}
                    title="Télécharger PDF"
                    actionType="Download"
                  />
                </Box>
              )}
            </Box>
          </LogoSection>
        </HeaderSection>

        <MissionInfoSection>
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <InfoRow>
                <SmallInfoCard iconName="building" title="Client" value={activitySummary.clientSocietyName} />
              </InfoRow>
            </Grid>
            <Grid item size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <InfoRow>
                <SmallInfoCard iconName="puzzle" title="Mission" value={activitySummary.missionLabel} />
              </InfoRow>
            </Grid>
            <Grid item size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <InfoRow>
                <SmallInfoCard iconName="people" title="Bloomer" value={activitySummary.bloomerFullName} />
              </InfoRow>
            </Grid>
            <Grid item size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <InfoRow>
                <SmallInfoCard iconName="clock" title="Base horaire" value={activitySummary.hoursPerDays} />
              </InfoRow>
            </Grid>
            <Grid item size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <InfoRow>
                <SmallInfoCard iconName="calendar" title="Semaine" value={activitySummary.nbDaysWorked} />
              </InfoRow>
            </Grid>
            <Grid item size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <InfoRow>
                <SmallInfoCard iconName="calendar" title="Samedi" value={activitySummary.nbDaysMarkupWorked} />
              </InfoRow>
            </Grid>
            <Grid item size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <InfoRow>
                <SmallInfoCard iconName="calendar" title="Dimanche/Férié" value={activitySummary.nbDaysMarkup2Worked} />
              </InfoRow>
            </Grid>
            <Grid item size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <InfoRow>
                <SmallInfoCard iconName="calendar" title="Astreinte" value={activitySummary.astreinte} />
              </InfoRow>
            </Grid>
          </Grid>
        </MissionInfoSection>
        <Typography variant="heading5" sx={{ marginBottom: 2 }}>
          Soit un total de {totalDays} jour{totalDays > 1 ? 's' : ''}
        </Typography>
        {!readOnly && (
          <SignatureSection>
            <Grid container spacing={2} sx={{ flexGrow: 1 }}>
              <Grid item size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                <StyledCRASignature>
                  <CRASignature
                    signatureRole="bloomer"
                    isSigned={isBloomerSigned}
                    loading={loading}
                    signedName={bloomerSignature.signerName}
                    signedAt={bloomerSignature.signatureDate}
                    expectedTotalDays={Number(totalDays)}
                    onSign={async (payload: BaseSignatureData) => {
                      setBloomerSignature(payload);
                      await handleBloomerSign(payload);
                    }}
                  />
                </StyledCRASignature>
              </Grid>
              <Grid item size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                <StyledCRASignature>
                  <CRASignature
                    signatureRole="client"
                    isSigned={isClientSigned}
                    loading={loading}
                    viewOnly={!isBloomerSigned}
                    signedName={clientSignature.signerName}
                    signedAt={clientSignature.signatureDate}
                    expectedTotalDays={Number(totalDays)}
                    onSign={async (payload: BaseSignatureData) => {
                      setClientSignature(payload);
                      await handleClientSign(payload);
                    }}
                  />
                </StyledCRASignature>
              </Grid>
            </Grid>
          </SignatureSection>
        )}

        {readOnly && (isBloomerSigned || isClientSigned) && (
          <SignatureSection>
            <Grid container spacing={2} alignItems="stretch">
              {isBloomerSigned && (
                <Grid item size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                  <StyledCRASignature>
                    <CRASignature
                      signatureRole="bloomer"
                      isSigned={true}
                      signedName={bloomerSignature.signerName}
                      signedAt={bloomerSignature.signatureDate}
                      viewOnly={true}
                    />
                  </StyledCRASignature>
                </Grid>
              )}
              {isClientSigned && (
                <Grid item size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                  <StyledCRASignature>
                    <CRASignature
                      signatureRole="client"
                      isSigned={true}
                      signedName={clientSignature.signerName}
                      signedAt={clientSignature.signatureDate}
                      viewOnly={true}
                    />
                  </StyledCRASignature>
                </Grid>
              )}
            </Grid>
          </SignatureSection>
        )}
        <Typography variant="heading5" sx={{ marginTop: 2 }}>
          Détail des jours
        </Typography>
        <FormCRA
          eachDays={activityDays}
          hoursPerDay={activitySummary.hoursPerDays}
          readOnly={true}
          onCRAUpdate={() => void 0}
        />

        {onClose && (
          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 3 }}>
            <Button textButton="Fermer" onClick={onClose} color="secondary" />
          </Box>
        )}
      </Container>
    </>
  ) as React.ReactNode;
};

export default CRAValidation;
